import { useCallback, useEffect, useRef, useState } from "react";
import { publicApi } from "@/lib/api";
import { JENIS_KELAMIN, ADA_TIDAK, E_KMKB, JAMINAN, KELAS_JAMINAN } from "@/lib/constants";
import { validatePengajuan, formatRm, formatPhone, todayWib, addDays, MAX_HARI_KE_DEPAN, NAMA_PASIEN_MAX, NAMA_PASIEN_WARN } from "@/lib/pengajuanValidation";
import CharCounter from "@/components/CharCounter";
import { fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, AlertCircle, ShieldCheck } from "lucide-react";
import AppLogo from "@/components/AppLogo";
import DpjpCombobox from "@/components/DpjpCombobox";

// Rawat Inap can read LOS Pasien, so steer doctors to a duration only, not clinical notes.
const LOS_PLACEHOLDER = "Contoh: 3 hari / 5 hari";
const LOS_HINT = "Isi lama rawat saja, bukan catatan klinis.";

const DPJP_PILIH_MSG = "Pilih DPJP dari daftar, atau pilih \u201cLainnya (isi manual)\u201d di bawah daftar.";

// "JKN" is not offered here (staff can still record/edit it in the dashboard for older cases).
const JAMINAN_PUBLIK = JAMINAN.filter((j) => j !== "JKN");

const DOCTOR_FIELDS = { nama_dokter: "", no_hp_dokter: "", email_dokter: "" };
const PATIENT_FIELDS = {
  nama_pasien: "", no_rm: "", umur: "", jenis_kelamin: "", no_telp: "", espr: "", e_kmkb: "", los_pasien: "",
  ksm_id: "", dpjp_id: "", dpjp_nama: "", jaminan: "", kelas_jaminan: "", diagnosa: "", rencana_tindakan: "",
  tanggal_operasi: "", tanggal_masuk_rawat_inap: "",
};
const FIELD_ORDER = [
  "nama_dokter", "no_hp_dokter", "email_dokter", "nama_pasien", "no_rm", "umur", "jenis_kelamin", "no_telp", "espr", "e_kmkb", "los_pasien",
  "ksm_id", "dpjp_nama", "jaminan", "kelas_jaminan", "diagnosa", "rencana_tindakan",
  "tanggal_operasi", "tanggal_masuk_rawat_inap",
];

const Field = ({ id, label, hint, error, children }) => (
  <div className="space-y-1.5" data-field={id}>
    <Label htmlFor={id} className="text-xs font-medium text-slate-600">
      {label} <span className="text-red-500">*</span>
    </Label>
    {children}
    {hint && !error && <p className="text-xs text-slate-500" id={`${id}-hint`}>{hint}</p>}
    {error && <p className="text-xs text-red-600" data-testid={`error-${id}`}>{error}</p>}
  </div>
);

const Section = ({ title, children }) => (
  <div className="space-y-4">
    <h2 className="font-heading text-sm font-bold text-[#267B84] uppercase tracking-wide">{title}</h2>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>
  </div>
);

function submitError(err) {
  const status = err?.response?.status;
  const d = err?.response?.data?.detail;
  if (!err?.response) return "Tidak dapat terhubung ke server. Periksa koneksi internet Anda lalu coba lagi.";
  if (status === 429) {
    const wait = Number(err.response.headers?.["retry-after"]);
    const hint = wait ? ` Coba lagi sekitar ${wait >= 90 ? `${Math.ceil(wait / 60)} menit` : `${wait} detik`} lagi.` : "";
    return (typeof d === "string" ? d : "Terlalu banyak permintaan.") + (typeof d === "string" && d.includes("Coba lagi") ? "" : hint);
  }
  if (typeof d === "string") return d;
  if (d?.message) return d.message;
  if (status === 413) return "Data terlalu besar.";
  return "Pengajuan gagal dikirim. Silakan coba lagi.";
}

export default function AjukanPage() {
  const [config, setConfig] = useState(null);       // { aktif, token, turnstile_site_key }
  const [configError, setConfigError] = useState("");
  const [ksmList, setKsmList] = useState([]);
  const [dpjpList, setDpjpList] = useState([]);     // Master DPJP (all active Departemen), for the combobox
  const [dpjpFailed, setDpjpFailed] = useState(false);   // list unavailable: the field falls back to a typed name
  const [dpjpManual, setDpjpManual] = useState(false);   // "Lainnya (isi manual)" chosen
  const [form, setForm] = useState({ ...DOCTOR_FIELDS, ...PATIENT_FIELDS });
  const [errors, setErrors] = useState({});
  const [banner, setBanner] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(null);           // { kode }
  const [hp, setHp] = useState("");                 // honeypot
  const [tsToken, setTsToken] = useState(null);
  const tsRef = useRef(null);
  const tsWidget = useRef(null);

  const today = todayWib();
  const maxDate = addDays(today, MAX_HARI_KE_DEPAN);

  const loadConfig = useCallback(async () => {
    try {
      const { data } = await publicApi.get("/public/form");
      setConfig(data);
      setConfigError("");
      return data;
    } catch (e) {
      setConfigError(e?.response?.status === 429
        ? "Terlalu banyak permintaan. Coba lagi beberapa saat lagi."
        : "Formulir tidak dapat dimuat. Periksa koneksi internet lalu muat ulang halaman.");
      return null;
    }
  }, []);

  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Form Pengajuan Pasien — TERPADU-OP RSCM";
    loadConfig();
    publicApi.get("/public/ksm").then((r) => setKsmList(r.data)).catch(() => {});
    publicApi.get("/public/dpjp").then((r) => setDpjpList(r.data)).catch(() => setDpjpFailed(true));
    return () => { document.title = prevTitle; };
  }, [loadConfig]);

  // Cloudflare Turnstile: only when the backend has a secret configured (site key is served by the API).
  const siteKey = config?.turnstile_site_key;
  useEffect(() => {
    if (!siteKey || done || !config?.aktif) return undefined;
    let cancelled = false;
    const render = () => {
      if (cancelled || !tsRef.current || !window.turnstile || tsWidget.current !== null) return;
      tsWidget.current = window.turnstile.render(tsRef.current, {
        sitekey: siteKey,
        callback: (t) => setTsToken(t),
        "expired-callback": () => setTsToken(null),
        "error-callback": () => setTsToken(null),
      });
    };
    if (window.turnstile) render();
    else if (!document.getElementById("cf-turnstile-script")) {
      const s = document.createElement("script");
      s.id = "cf-turnstile-script";
      s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      s.async = true;
      s.onload = render;
      document.head.appendChild(s);
    } else {
      document.getElementById("cf-turnstile-script").addEventListener("load", render);
    }
    return () => {
      cancelled = true;
      if (tsWidget.current !== null && window.turnstile) window.turnstile.remove(tsWidget.current);
      tsWidget.current = null;
    };
  }, [siteKey, done, config?.aktif]);

  const resetTurnstile = () => {
    setTsToken(null);
    if (tsWidget.current !== null && window.turnstile) window.turnstile.reset(tsWidget.current);
  };

  const set = (key, val) => {
    setForm((f) => ({ ...f, [key]: val }));
    if (errors[key]) setErrors((e) => { const { [key]: _drop, ...rest } = e; return rest; });
  };

  const dpjpOptions = dpjpList.filter((d) => d.ksm_id === form.ksm_id);
  // No list for this Departemen (or none loaded): the DPJP is typed by hand, as before.
  const dpjpManualOnly = dpjpFailed || (Boolean(form.ksm_id) && dpjpOptions.length === 0);

  const setDpjp = ({ dpjp_id, dpjp_nama, manual }) => {
    setForm((f) => ({ ...f, dpjp_id: dpjp_id || "", dpjp_nama }));
    setDpjpManual(manual);
    if (errors.dpjp_nama) setErrors((e) => { const { dpjp_nama: _drop, ...rest } = e; return rest; });
  };

  const scrollToFirstError = (errs) => {
    const first = FIELD_ORDER.find((k) => errs[k]);
    if (first) document.querySelector(`[data-field="${first}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBanner("");
    const errs = validatePengajuan(form, today);
    if (form.dpjp_id) delete errs.dpjp_nama;                  // the Master name is used as is
    else if (form.ksm_id && !dpjpManual && !dpjpManualOnly) errs.dpjp_nama = DPJP_PILIH_MSG;
    if (Object.keys(errs).length) {
      setErrors(errs);
      setBanner("Periksa kembali isian yang ditandai merah.");
      scrollToFirstError(errs);
      return;
    }
    if (siteKey && !tsToken) {
      setBanner("Selesaikan verifikasi keamanan terlebih dahulu.");
      return;
    }
    setErrors({});
    setSaving(true);
    try {
      const { data } = await publicApi.post("/public/pengajuan", {
        ...form,
        dpjp_id: form.dpjp_id || null,
        umur: parseInt(form.umur, 10),
        form_token: config.token,
        turnstile_token: tsToken || undefined,
        alamat_kantor: hp,
      });
      setDone({ kode: data.kode, waktu: data.waktu_pengajuan });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (err?.response?.status === 422 && d?.errors) {
        setErrors(d.errors);
        setBanner(d.message || "Periksa kembali isian Anda.");
        scrollToFirstError(d.errors);
      } else {
        setBanner(submitError(err));
        if (err?.response?.status === 400) await loadConfig(); // token expired/spent: get a fresh one
      }
      resetTurnstile();
    } finally {
      setSaving(false);
    }
  };

  const another = async () => {
    setForm((f) => ({ ...PATIENT_FIELDS, nama_dokter: f.nama_dokter, no_hp_dokter: f.no_hp_dokter, email_dokter: f.email_dokter }));  // keep doctor, in memory only
    setErrors({}); setBanner(""); setDone(null); setHp(""); setDpjpManual(false);
    await loadConfig();
    window.scrollTo({ top: 0 });
  };

  const selectCls = "bg-white";
  const fe = (k) => errors[k];
  const ring = (k) => (errors[k] ? "border-red-400 focus-visible:ring-red-300" : "");

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <div className="mx-auto w-full max-w-2xl px-4 py-8 sm:py-12">
        <div className="flex items-center gap-3 mb-6">
          <AppLogo />
          <div>
            <p className="font-heading font-bold text-slate-900 leading-tight">TERPADU-OP RSCM</p>
            <p className="text-xs text-slate-500">Rumah Sakit Cipto Mangunkusumo</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 sm:p-8" data-testid="ajukan-card">
          {done ? (
            <div className="text-center py-6 space-y-4" data-testid="ajukan-success">
              <CheckCircle2 className="mx-auto text-[#00B9AD]" size={48} />
              <h1 className="font-heading text-2xl font-bold text-slate-900">Pengajuan diterima</h1>
              <p className="text-sm text-slate-600">
                Data pasien sudah masuk ke Jadwal Pasien. Status kamar rawat inap akan ditindaklanjuti oleh
                Case Manager (jam kerja) atau Petugas Admisi (di luar jam kerja).
              </p>
              <div className="inline-block rounded-xl border border-slate-200 bg-slate-50 px-5 py-3">
                <p className="text-xs text-slate-500">Kode pengajuan</p>
                <p className="font-mono text-2xl font-bold tracking-wider text-slate-900" data-testid="ajukan-kode">{done.kode}</p>
                {done.waktu && (
                  <p className="text-xs text-slate-500 mt-1.5" data-testid="ajukan-waktu">
                    Diajukan pada {fmtDateTime(done.waktu)} WIB
                  </p>
                )}
              </div>
              <p className="text-xs text-slate-500" data-testid="ajukan-simpan-kode">
                Screenshot atau catat kode ini sebagai bukti pengajuan, dan sebutkan bila menghubungi Admisi/Case Manager.
                Tidak ada email konfirmasi pengajuan; email hanya dikirim saat pasien sudah mendapat kamar.
              </p>
              <div>
                <Button onClick={another} className="bg-[#00B9AD] hover:bg-[#009E94] text-white" data-testid="ajukan-lagi">
                  Ajukan pasien lain
                </Button>
              </div>
            </div>
          ) : configError ? (
            <div className="text-center py-8 space-y-4" data-testid="ajukan-config-error">
              <AlertCircle className="mx-auto text-red-500" size={40} />
              <p className="text-sm text-slate-700">{configError}</p>
              <Button variant="outline" onClick={loadConfig}>Coba lagi</Button>
            </div>
          ) : config && !config.aktif ? (
            <div className="text-center py-8 space-y-3" data-testid="ajukan-disabled">
              <AlertCircle className="mx-auto text-amber-500" size={40} />
              <h1 className="font-heading text-xl font-bold text-slate-900">Pengajuan online sedang tidak tersedia</h1>
              <p className="text-sm text-slate-600">Silakan hubungi Admisi atau Case Manager untuk mengajukan pasien.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-7" noValidate data-testid="ajukan-form">
              <div>
                <h1 className="font-heading text-2xl font-bold text-slate-900">Form Pengajuan Pasien</h1>
                <p className="text-sm text-slate-500 mt-1 text-justify">
                  Untuk dokter pengaju/<wbr />PPDS. Data masuk langsung ke Jadwal Pasien. Status kamar tidak perlu diisi —
                  akan diisi oleh Case&nbsp;Manager/<wbr />Petugas Admisi. Semua isian bertanda&nbsp;<span className="text-red-500">*</span>&nbsp;wajib.
                </p>
              </div>

              {banner && (
                <div className="flex gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3.5 py-2.5" role="alert" data-testid="ajukan-banner">
                  <AlertCircle size={16} className="mt-0.5 shrink-0" /> <span>{banner}</span>
                </div>
              )}

              <Section title="Dokter Pengaju">
                <Field id="nama_dokter" label="Nama Dokter (Pengaju)" error={fe("nama_dokter")}>
                  <Input id="nama_dokter" autoComplete="name" value={form.nama_dokter} maxLength={100}
                    onChange={(e) => set("nama_dokter", e.target.value)} className={ring("nama_dokter")} data-testid="ajukan-nama-dokter" />
                </Field>
                <Field id="no_hp_dokter" label="No. HP Dokter (Pengaju)" error={fe("no_hp_dokter")}>
                  <Input id="no_hp_dokter" type="tel" inputMode="numeric" autoComplete="tel" placeholder="0812 1234 5678" value={form.no_hp_dokter}
                    onChange={(e) => set("no_hp_dokter", formatPhone(e.target.value))} className={ring("no_hp_dokter")} data-testid="ajukan-no-hp-dokter" />
                </Field>
                <Field id="email_dokter" label="Email Dokter (Pengaju)" error={fe("email_dokter")}>
                  <Input id="email_dokter" type="email" inputMode="email" autoComplete="email" placeholder="nama@domain.com" value={form.email_dokter}
                    maxLength={254} onChange={(e) => set("email_dokter", e.target.value)} className={ring("email_dokter")} data-testid="ajukan-email-dokter" />
                </Field>
              </Section>

              <Section title="Data Pasien">
                <Field id="nama_pasien" label="Nama Pasien" error={fe("nama_pasien")}>
                  <Input id="nama_pasien" value={form.nama_pasien} autoComplete="off"
                    onChange={(e) => set("nama_pasien", e.target.value)} className={ring("nama_pasien")} data-testid="ajukan-nama-pasien" />
                  <CharCounter length={form.nama_pasien.trim().length} max={NAMA_PASIEN_MAX} warnAt={NAMA_PASIEN_WARN} testId="ajukan-nama-pasien-counter" />
                </Field>
                <Field id="no_rm" label="No. RM" error={fe("no_rm")}>
                  <Input id="no_rm" inputMode="numeric" placeholder="123-45-67" value={form.no_rm} maxLength={9} autoComplete="off"
                    onChange={(e) => set("no_rm", formatRm(e.target.value))} className={`font-mono ${ring("no_rm")}`} data-testid="ajukan-no-rm" />
                </Field>
                <Field id="umur" label="Umur (tahun)" error={fe("umur")}>
                  <Input id="umur" inputMode="numeric" value={form.umur} maxLength={3} autoComplete="off"
                    onChange={(e) => set("umur", e.target.value.replace(/\D/g, ""))} className={ring("umur")} data-testid="ajukan-umur" />
                </Field>
                <Field id="jenis_kelamin" label="Jenis Kelamin" error={fe("jenis_kelamin")}>
                  <Select value={form.jenis_kelamin} onValueChange={(v) => set("jenis_kelamin", v)}>
                    <SelectTrigger id="jenis_kelamin" className={ring("jenis_kelamin")} data-testid="ajukan-jenis-kelamin"><SelectValue placeholder="Pilih jenis kelamin" /></SelectTrigger>
                    <SelectContent className={selectCls}>{JENIS_KELAMIN.map((j) => <SelectItem key={j} value={j}>{j}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field id="no_telp" label="No. Telp yang bisa dihubungi" error={fe("no_telp")}>
                  <Input id="no_telp" type="tel" inputMode="numeric" placeholder="0812 1234 5678" value={form.no_telp} autoComplete="off"
                    onChange={(e) => set("no_telp", formatPhone(e.target.value))} className={ring("no_telp")} data-testid="ajukan-no-telp" />
                </Field>
                <Field id="espr" label="E-SPR" error={fe("espr")}>
                  <Select value={form.espr} onValueChange={(v) => set("espr", v)}>
                    <SelectTrigger id="espr" className={ring("espr")} data-testid="ajukan-espr"><SelectValue placeholder="Pilih" /></SelectTrigger>
                    <SelectContent className={selectCls}>{ADA_TIDAK.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field id="e_kmkb" label="E-KMKB" error={fe("e_kmkb")}>
                  <Select value={form.e_kmkb} onValueChange={(v) => set("e_kmkb", v)}>
                    <SelectTrigger id="e_kmkb" className={ring("e_kmkb")} data-testid="ajukan-e-kmkb"><SelectValue placeholder="Pilih" /></SelectTrigger>
                    <SelectContent className={selectCls}>{E_KMKB.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field id="los_pasien" label="LOS Pasien (estimasi lama rawat)" hint={LOS_HINT} error={fe("los_pasien")}>
                  <Input id="los_pasien" value={form.los_pasien} maxLength={200} autoComplete="off" placeholder={LOS_PLACEHOLDER}
                    aria-describedby="los_pasien-hint"
                    onChange={(e) => set("los_pasien", e.target.value)} className={ring("los_pasien")} data-testid="ajukan-los-pasien" />
                </Field>
              </Section>

              <Section title="Rencana Operasi/Tindakan">
                <Field id="ksm_id" label="Departemen" error={fe("ksm_id")}>
                  <Select value={form.ksm_id} onValueChange={(v) => {
                    set("ksm_id", v);
                    if (v !== form.ksm_id) setDpjp({ dpjp_id: null, dpjp_nama: "", manual: false });   // the list depends on it
                  }}>
                    <SelectTrigger id="ksm_id" className={ring("ksm_id")} data-testid="ajukan-ksm"><SelectValue placeholder="Pilih Departemen" /></SelectTrigger>
                    <SelectContent className={selectCls}>{ksmList.map((k) => <SelectItem key={k.id} value={k.id}>{k.nama}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field id="dpjp_nama" label="DPJP" error={fe("dpjp_nama")}
                  hint={form.ksm_id && !dpjpManualOnly && !dpjpManual ? "Ketik sebagian nama, lalu pilih dari daftar." : undefined}>
                  <DpjpCombobox id="dpjp_nama" options={dpjpOptions} disabled={!form.ksm_id} manualOnly={dpjpManualOnly}
                    value={{ dpjp_id: form.dpjp_id, dpjp_nama: form.dpjp_nama, manual: dpjpManual }} onChange={setDpjp}
                    invalid={Boolean(fe("dpjp_nama"))} describedBy="dpjp_nama-hint" testId="ajukan-dpjp" />
                </Field>
                <Field id="jaminan" label="Jaminan" error={fe("jaminan")}>
                  <Select value={form.jaminan} onValueChange={(v) => {
                    set("jaminan", v);
                    if (v !== "BPJS") set("kelas_jaminan", "");
                  }}>
                    <SelectTrigger id="jaminan" className={ring("jaminan")} data-testid="ajukan-jaminan"><SelectValue placeholder="Pilih jaminan" /></SelectTrigger>
                    <SelectContent className={selectCls}>{JAMINAN_PUBLIK.map((j) => <SelectItem key={j} value={j}>{j}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                {form.jaminan === "BPJS" && (
                  <Field id="kelas_jaminan" label="Kelas Jaminan" error={fe("kelas_jaminan")}>
                    <Select value={form.kelas_jaminan} onValueChange={(v) => set("kelas_jaminan", v)}>
                      <SelectTrigger id="kelas_jaminan" className={ring("kelas_jaminan")} data-testid="ajukan-kelas-jaminan"><SelectValue placeholder="Pilih kelas" /></SelectTrigger>
                      <SelectContent className={selectCls}>{KELAS_JAMINAN.map((k) => <SelectItem key={k} value={k}>Kelas {k}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                )}
                <Field id="tanggal_operasi" label="Tanggal Jadwal Operasi/Tindakan" error={fe("tanggal_operasi")}>
                  <Input id="tanggal_operasi" type="date" min={today} max={maxDate} value={form.tanggal_operasi}
                    onChange={(e) => set("tanggal_operasi", e.target.value)} className={ring("tanggal_operasi")} data-testid="ajukan-tanggal-operasi" />
                </Field>
                <Field id="tanggal_masuk_rawat_inap" label="Tanggal Masuk Rawat Inap" error={fe("tanggal_masuk_rawat_inap")}>
                  <Input id="tanggal_masuk_rawat_inap" type="date" min={today} max={maxDate} value={form.tanggal_masuk_rawat_inap}
                    onChange={(e) => set("tanggal_masuk_rawat_inap", e.target.value)} className={ring("tanggal_masuk_rawat_inap")} data-testid="ajukan-tanggal-masuk-ranap" />
                </Field>
                <div className="sm:col-span-2">
                  <Field id="diagnosa" label="Diagnosa" error={fe("diagnosa")}>
                    <Textarea id="diagnosa" rows={3} maxLength={500} value={form.diagnosa}
                      onChange={(e) => set("diagnosa", e.target.value)} className={ring("diagnosa")} data-testid="ajukan-diagnosa" />
                  </Field>
                </div>
                <div className="sm:col-span-2">
                  <Field id="rencana_tindakan" label="Rencana Tindakan" error={fe("rencana_tindakan")}>
                    <Textarea id="rencana_tindakan" rows={3} maxLength={500} value={form.rencana_tindakan}
                      onChange={(e) => set("rencana_tindakan", e.target.value)} className={ring("rencana_tindakan")} data-testid="ajukan-rencana-tindakan" />
                  </Field>
                </div>
              </Section>

              {/* Honeypot: invisible to people, tempting to bots */}
              <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", width: 1, height: 1, overflow: "hidden" }}>
                <label>Alamat kantor
                  <input type="text" name="alamat_kantor" tabIndex={-1} autoComplete="off" value={hp} onChange={(e) => setHp(e.target.value)} />
                </label>
              </div>

              {siteKey && <div ref={tsRef} data-testid="ajukan-turnstile" />}

              <div className="space-y-3">
                <Button type="submit" disabled={saving || !config} className="w-full h-11 bg-[#00B9AD] hover:bg-[#009E94] text-white font-medium" data-testid="ajukan-submit">
                  {saving ? "Mengirim..." : "Kirim Pengajuan"}
                </Button>
                <p className="flex gap-1.5 text-[11px] leading-relaxed text-slate-400">
                  <ShieldCheck size={13} className="mt-0.5 shrink-0" />
                  Data hanya untuk keperluan penjadwalan operasi RSCM. Halaman ini hanya menerima data dan tidak menampilkan data pasien.
                </p>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
